import {
  BetaTag,
  Body,
  Heading,
  Icon,
  IconButton,
  MobileSheet,
  Navbar,
  NavbarAdminSection,
  NavbarProfileMenuItem,
  type NavbarTabType,
  NotificationBar,
  NotificationCounter,
} from "@indigo-components";
import { FloatButton, message } from "antd";
import { clsx } from "clsx";
import { useAtom } from "jotai";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Helmet } from "react-helmet";
import {
  ImperativePanelHandle,
  Panel,
  PanelGroup,
  PanelResizeHandle,
} from "react-resizable-panels";
import { Outlet, useNavigate } from "react-router-dom";
import {
  useMarkAllNotificationsAsRead,
  useMarkNotificationAsRead,
  useMarkNotificationAsUnread,
  useNotifications,
} from "~/api/queries/notifications";
import { chatbotOpenAtom } from "~/atoms/chatbot";
import { navbarPinnedAtom } from "~/atoms/navbar";
import { notificationBarOpenAtom } from "~/atoms/notifications";
import { sidebarPinnedAtom } from "~/atoms/sidebar";
import { useAuthSession } from "~/auth/AuthSessionProvider";
import { useCurrentUser } from "~/auth/UserContext";
import { getTabTitle } from "~/common/converters";
import BrowserWarningBanner from "~/components/BrowserWarningBanner";
import ImpersonationBanner from "~/components/ImpersonationBanner";
import SandboxOverrideBanner from "~/components/SandboxOverrideBanner";
import SessionExpiredBanner from "~/components/SessionExpiredBanner";
import { errorReporter } from "~/error-handling/errorReporter";
import { toError } from "~/error-handling/errorUtils";
import { useChatbotContext } from "~/hooks/useChatbotContext";
import { useIsSmallScreen } from "~/hooks/useIsSmallScreen";
import { useKeyboardShortcuts } from "~/hooks/useKeyboardShortcuts";
import { useNavigationBreadcrumbs } from "~/hooks/useNavigationBreadcrumbs";
import { useSessionExpiry } from "~/hooks/useSessionExpiry";
import { useSessionExpiryRedirect } from "~/hooks/useSessionExpiryRedirect";
import { useTypedLocation } from "~/hooks/useTypedLocation";
import { TAILWIND_BREAKPOINTS } from "~/indigo-components/src/styles/breakpoints";
import ChatbotLauncher from "~/pages/appointments/components/ChatbotLauncher";
import ChatbotModal, {
  IndyLabel,
} from "~/pages/appointments/components/ChatbotModal";
import { canAccessIndy } from "~/pages/indy/indyAccess";
import { useFeatureFlag } from "~/services/featureFlags";
import { FEATURE_FLAGS } from "~/services/featureFlags/constants";
import { AuthUserGroup } from "~/types/auth";
import { IS_MAC } from "~/utils/platform";
import KeyboardShortcutsModal from "../modals/KeyboardShortcutsModal";
import UserSettingsModal from "../modals/UserSettingsModal";
import {
  NOTIFICATION_BAR_MAX_SIZE,
  NOTIFICATION_BAR_MIN_SIZE,
  NOTIFICATION_TYPE_CONFIG,
} from "./constants";
import { getNavbarTabs } from "./utils";

/** Path of the full-screen Indy route. Referenced in several places (navbar
 *  tab, full-screen layout branch, modal suppression) — keep them in sync. */
const INDY_PATH = "/indy";

const MainLayout = () => {
  const { user } = useCurrentUser();
  const [navbarPinned, setNavbarPinned] = useAtom(navbarPinnedAtom);
  const [notificationBarOpen, setNotificationBarOpen] = useAtom(
    notificationBarOpenAtom,
  );
  const [sidebarPinned] = useAtom(sidebarPinnedAtom);
  const [chatbotOpen, setChatbotOpen] = useAtom(chatbotOpenAtom);
  // Same cut as .indy-launcher's @media (max-width: 640px), so the BackTop
  // offset below tracks the launcher into its mobile corner (right/bottom 16).
  // NUMERIC token on purpose: the string form ("sm") subtracts 1 in
  // getMaxWidthForBreakpoint and would sit 1px off the CSS query.
  const isNarrowViewport = useIsSmallScreen(TAILWIND_BREAKPOINTS.sm);
  // Separate, wider cut than the launcher's above: the navbar rail stops
  // being affordable well before 640px — at 375px its collapsed 64px is 17%
  // of the viewport, spent on a strip of icons. Below `md` it becomes an
  // off-canvas panel and the content takes the full width.
  const isMobileLayout = useIsSmallScreen("md");
  const [isNavSheetOpen, setIsNavSheetOpen] = useState(false);
  const chatContext = useChatbotContext();
  const { pathname } = useTypedLocation();
  const isLibraryActive = pathname === "/library";

  // Get the auth session for user role
  const { userGroup, signOut } = useAuthSession();
  const sessionExpiry = useSessionExpiry();

  const profileWarning = useMemo(() => {
    // Internal staff only for now (Admin, MA, Historian). This proactive tooltip
    // likely needs a better design before we show external doctors — the
    // reactive expired-session banner already covers all roles.
    if (userGroup === AuthUserGroup.DOCTOR) {
      return undefined;
    }
    if (!sessionExpiry.isWarning || sessionExpiry.remainingLabel === null) {
      return undefined;
    }
    return {
      tooltip: (
        <div style={{ maxWidth: 240 }}>
          <Heading level={4} className="mb-1 text-indigo-text-on-accent">
            Session expiring soon
          </Heading>
          <Body size="sm" className="text-indigo-text-on-accent">
            You&apos;ll be signed out in {sessionExpiry.remainingLabel}. Save
            your work and sign back in to keep working.
          </Body>
        </div>
      ),
    };
  }, [userGroup, sessionExpiry.isWarning, sessionExpiry.remainingLabel]);

  // Modal states
  const [isSettingsModalOpen, setIsSettingsModalOpen] = useState(false);
  const [isShortcutsModalOpen, setIsShortcutsModalOpen] = useState(false);

  // Determine if user can see settings/shortcuts
  const showUserMenu =
    userGroup === AuthUserGroup.ADMIN ||
    userGroup === AuthUserGroup.MEDICAL_ASSISTANT ||
    userGroup === AuthUserGroup.HISTORIAN;

  // Check if library feature is enabled
  const { isEnabled: isLibraryFeatureEnabled } = useFeatureFlag(
    FEATURE_FLAGS.LIBRARY,
  );
  const { isEnabled: isNotificationsFeatureEnabled } = useFeatureFlag(
    FEATURE_FLAGS.NOTIFICATIONS,
  );
  const { isEnabled: isChatbotEnabled } = useFeatureFlag(
    FEATURE_FLAGS.ENABLED_CHATBOT,
  );
  // Debug tools (page + Ctrl+Alt+L shortcut) are open to all non-doctor staff,
  // matching the /debug route gate. No feature flag.
  const canAccessDebug =
    userGroup === AuthUserGroup.ADMIN ||
    userGroup === AuthUserGroup.MEDICAL_ASSISTANT ||
    userGroup === AuthUserGroup.HISTORIAN;
  const { isEnabled: isAllCasesDashboardEnabled } = useFeatureFlag(
    FEATURE_FLAGS.ALL_CASES_DASHBOARD,
  );
  const { isEnabled: isReferralEnabled } = useFeatureFlag(
    FEATURE_FLAGS.REFERRAL,
  );

  const handleLogout = useCallback(() => {
    try {
      signOut?.();

      // Clear the design system and theme state from localStorage
      localStorage.removeItem("isLightTheme");
    } catch (error) {
      errorReporter.reportApplicationError(
        toError(error, "Error during logout"),
      );
    }
  }, [signOut]);

  // Automatically track navigation breadcrumbs for all route changes
  useNavigationBreadcrumbs();

  // When an expired-session user navigates to a new route, send them to /login.
  useSessionExpiryRedirect();

  // ── Notifications ────────────────────────────────────────────────────────
  const {
    notifications,
    unreadCount,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
    isLoading: isNotificationsLoading,
  } = useNotifications(isNotificationsFeatureEnabled);
  const { mutate: markAsRead } = useMarkNotificationAsRead();
  const { mutate: markAsUnread } = useMarkNotificationAsUnread();
  const { mutate: markAllAsRead, isPending: isMarkAllAsReadPending } =
    useMarkAllNotificationsAsRead();
  const navigate = useNavigate();

  const toggleNotificationBar = useCallback(() => {
    setNotificationBarOpen((prev) => !prev);
  }, [setNotificationBarOpen]);

  const handleNotificationClick = useCallback(
    (notification: {
      id: string;
      appointmentRecordID: string;
      readAt?: string | null;
    }) => {
      if (!notification.readAt) {
        markAsRead(notification.id);
      }
      navigate(`/appointments/${notification.appointmentRecordID}`);
    },
    [markAsRead, navigate],
  );

  const libraryTag = isLibraryFeatureEnabled ? (
    <BetaTag
      size="sm"
      label="NEW!"
      variant={isLibraryActive ? "on-dark" : "default"}
    />
  ) : undefined;

  const baseTabs = getNavbarTabs({
    userGroup,
    isLibraryEnabled: isLibraryFeatureEnabled,
    isAllCasesDashboardEnabled,
    isReferralEnabled,
    libraryTag,
  });

  // Append dynamic Notifications tab for MA/Admin
  const showNotificationsTab =
    isNotificationsFeatureEnabled &&
    (userGroup === AuthUserGroup.MEDICAL_ASSISTANT ||
      userGroup === AuthUserGroup.ADMIN);

  // Gates every Indy entry point — the navbar tab, the floating panel, the
  // corner launcher, and Cmd+K. The role list is shared with the /indy route
  // guard via `canAccessIndy`, so an entry point can never point at a route
  // that 403s it (or vice versa); the flag stays here because the launcher is
  // global and a role-only check would show it before the flag resolves.
  const canUseIndy = isChatbotEnabled && canAccessIndy(userGroup);

  const navbarTabs: NavbarTabType[] = useMemo(() => {
    let tabs = baseTabs;

    if (showNotificationsTab) {
      const notificationsTab: NavbarTabType = {
        title: "Notifications",
        iconName: "notifications-tab" as const,
        onClick: toggleNotificationBar,
        isActive: notificationBarOpen,
        tag:
          unreadCount > 0 ? (
            <NotificationCounter count={unreadCount} />
          ) : undefined,
        showCollapsedDot: unreadCount > 0,
      };

      // Keep Notifications above Library when Library is enabled.
      const libraryIndex = tabs.findIndex(
        (tab) => "to" in tab && tab.to === "/library",
      );

      tabs =
        libraryIndex === -1
          ? [...tabs, notificationsTab]
          : [
              ...tabs.slice(0, libraryIndex),
              notificationsTab,
              ...tabs.slice(libraryIndex),
            ];
    }

    if (canUseIndy) {
      const indyTab: NavbarTabType = {
        title: "Ask Indy",
        titleNode: <IndyLabel />,
        iconName: "indy-glasses",
        onClick: () => {
          const search =
            chatContext?.type === "appointment"
              ? `?appointmentId=${encodeURIComponent(chatContext.id)}`
              : chatContext?.type === "indigo_doc"
                ? `?indigoDocId=${encodeURIComponent(chatContext.id)}`
                : "";
          navigate(`${INDY_PATH}${search}`, { state: { from: pathname } });
        },
        isActive: pathname === INDY_PATH,
      };

      // Insert before Library (same splice pattern as Notifications)
      const libraryIndex = tabs.findIndex(
        (tab) => "to" in tab && tab.to === "/library",
      );

      tabs =
        libraryIndex === -1
          ? [...tabs, indyTab]
          : [
              ...tabs.slice(0, libraryIndex),
              indyTab,
              ...tabs.slice(libraryIndex),
            ];
    }

    return tabs;
  }, [
    baseTabs,
    showNotificationsTab,
    toggleNotificationBar,
    notificationBarOpen,
    unreadCount,
    canUseIndy,
    navigate,
    chatContext,
    pathname,
  ]);

  const adminSection: NavbarAdminSection | undefined = useMemo(() => {
    const isAdminOrMa =
      userGroup === AuthUserGroup.ADMIN ||
      userGroup === AuthUserGroup.MEDICAL_ASSISTANT;
    // Section renders for all non-doctor staff; the admin-only items below stay
    // gated, but Debug is open to everyone who can reach it.
    if (!canAccessDebug) return undefined;
    return {
      title: "Indigo admin",
      iconName: "settings-tab",
      items: [
        ...(isAdminOrMa
          ? [
              {
                to: "/physicians",
                title: "Physicians",
                iconName: "physician-icon" as const,
              },
              {
                to: "/locations",
                title: "Locations",
                iconName: "location-icon" as const,
              },
              {
                to: "/contacts",
                title: "Contacts",
                iconName: "contact-book" as const,
              },
              {
                to: "/ops-settings",
                title: "Ops setting",
                iconName: "clipboard-list" as const,
              },
            ]
          : []),
        ...(userGroup === AuthUserGroup.ADMIN
          ? [
              {
                to: "/skills",
                title: "Skills",
                iconName: "golf" as const,
              },
            ]
          : []),
        {
          to: "/debug",
          title: "Debug",
          iconName: "search" as const,
        },
      ],
    };
  }, [userGroup, canAccessDebug]);

  // Build profile menu items
  const profileMenuItems: NavbarProfileMenuItem[] = useMemo(
    () => [
      ...(showUserMenu
        ? [
            {
              key: "settings",
              label: "User Settings",
              icon: <Icon name="settings" size="s" />,
              onClick: () => setIsSettingsModalOpen(true),
            },
            {
              key: "shortcuts",
              label: "Keyboard Shortcuts",
              icon: <Icon name="help" size="s" />,
              onClick: () => setIsShortcutsModalOpen(true),
            },
            { type: "divider" as const },
          ]
        : []),
      {
        key: "logout",
        label: "Logout",
        icon: <Icon name="power" size="s" />,
        onClick: handleLogout,
      },
    ],
    [showUserMenu, handleLogout],
  );

  // Global keyboard shortcuts
  useKeyboardShortcuts(
    useMemo(
      () => [
        // Open shortcuts modal (Cmd+Option+/ or Ctrl+Alt+/)
        {
          keyCombination: { key: "/", ctrlKey: true, altKey: true },
          handler: () => {
            if (showUserMenu) setIsShortcutsModalOpen(true);
          },
          preventDefault: true,
        },
        // Toggle the floating Indy panel (Cmd+K on Mac, Ctrl+K elsewhere).
        // One entry, picked per platform: the hook fires with allowInInputs +
        // preventDefault in the capture phase, so registering the ctrl variant
        // on a Mac would hijack the native Ctrl+K "kill to end of line" in
        // every text field — and toggling mid-draft unmounts the panel and
        // loses the draft. `allowInInputs` is required, not optional: with the
        // panel open the focus is almost always in the composer textarea, and
        // without it the shortcut could open Indy but never close it. Excluded
        // on /indy: that route renders no panel, so the shortcut would
        // silently flip hidden state that surfaces later.
        ...(canUseIndy && pathname !== INDY_PATH
          ? [
              {
                keyCombination: IS_MAC
                  ? { key: "k", metaKey: true }
                  : { key: "k", ctrlKey: true },
                handler: () => setChatbotOpen((o) => !o),
                preventDefault: true,
                allowInInputs: true,
              },
            ]
          : []),
        // Focus search bar (/)
        {
          keyCombination: { key: "/" },
          handler: () => {
            const searchInput = document.getElementById(
              "search-input",
            ) as HTMLInputElement;
            if (searchInput) {
              searchInput.focus();
            }
          },
          preventDefault: true,
        },
        // Debug prompts for the current appointment (Ctrl+Alt+L). Uses any
        // currently-highlighted text as the "search by output" term.
        {
          keyCombination: { key: "l", ctrlKey: true, altKey: true },
          handler: () => {
            if (!canAccessDebug) return;
            const match = pathname.match(/^\/appointments\/([\w-]+)/);
            const apptId = match?.[1];
            if (!apptId) {
              void message.warning(
                "Open an appointment first to debug its prompts",
              );
              return;
            }
            const selection = window.getSelection()?.toString().trim() ?? "";
            const params = new URLSearchParams({ appointmentId: apptId });
            if (selection) params.set("outputQuery", selection);
            window.open(
              `/debug/prompts?${params.toString()}`,
              "_blank",
              "noopener",
            );
          },
          preventDefault: true,
          allowInInputs: true,
        },
      ],
      [showUserMenu, canAccessDebug, pathname, canUseIndy, setChatbotOpen],
    ),
  );

  // Only apply sidebarPinned margin if on /appointments/:id
  const isAppointmentDetailsPage = /^\/appointments\/[\w-]+$/.test(pathname);
  const isNewAppointmentPage = /^\/appointments\/new(\/[\w-]+)?$/.test(
    pathname,
  );
  const isSidebarFullscreenPage = /^\/sidebar-fullscreen$/.test(pathname);
  // Physical-eval-template configurator paints its own canvas-shade background,
  // so the transparent spacer would show as a white strip above it — it manages
  // its own top padding instead.
  const isPhysicalEvalTemplatePage =
    /^\/physicians\/[\w-]+\/physical-eval-templates$/.test(pathname);

  // Add a top spacer for consistent padding, but exclude pages with custom layouts
  // that manage their own spacing, such as the Appointment Details and Sidebar Fullscreen pages.
  // TODO (Sompop): Refactor this. Ideally the pages should explicitly manage their own spacing.
  const hasTopSpacer =
    !isAppointmentDetailsPage &&
    !isSidebarFullscreenPage &&
    !isNewAppointmentPage &&
    !isPhysicalEvalTemplatePage;

  // Auto-collapse navbar when sidebar opens
  useEffect(() => {
    if (sidebarPinned && isAppointmentDetailsPage && navbarPinned) {
      setNavbarPinned(false);
    }
  }, [sidebarPinned, isAppointmentDetailsPage, navbarPinned, setNavbarPinned]);

  // The notification bar persists across navigation and notification clicks
  // by design — it is dismissed only via its explicit close button (or by
  // toggling the Notifications tab). We intentionally do not close it on
  // pathname changes.

  useEffect(() => {
    if (!isNotificationsFeatureEnabled) {
      setNotificationBarOpen(false);
    }
  }, [isNotificationsFeatureEnabled, setNotificationBarOpen]);

  const notificationPanelRef = useRef<ImperativePanelHandle>(null);

  // Sync panel collapse/expand with atom state.
  //
  // We depend on `pathname` as well so that if react-resizable-panels
  // force-collapsed the panel during a route transition (e.g. a heavy
  // first-time dashboard mount temporarily starves the main content
  // panel of its `minSize`), we re-expand it to match the atom once
  // layout settles. Without this, the bar silently disappears after
  // navigation even though the user never dismissed it.
  // biome-ignore lint/correctness/useExhaustiveDependencies: pre-existing; deps reviewed, auto-fix risks behavior change
  useEffect(() => {
    const panel = notificationPanelRef.current;
    if (!panel) return;
    if (notificationBarOpen) {
      if (panel.isCollapsed()) panel.expand();
    } else {
      if (!panel.isCollapsed()) panel.collapse();
    }
  }, [notificationBarOpen, pathname]);

  // Navbar margin offset (fixed navbar). None on the mobile layout — the
  // navbar is an overlay there and reserves no column.
  const navbarMargin =
    !isSidebarFullscreenPage && !isMobileLayout
      ? navbarPinned
        ? "ml-[200px]"
        : "ml-[64px]"
      : "";

  // Full-screen Indy: render the route alone, covering the navbar + panels for
  // a distraction-free workspace. All layout hooks above still run.
  if (pathname === INDY_PATH) {
    // Block-level wrapper (not flex) so FullPageLayout fills the full width.
    // `dvh`/`w-full`, not `h-screen w-screen`: on mobile `100vh` is the height
    // the page would have with the browser chrome hidden, so a `100vh` wrapper
    // is taller than the screen and leaves dead space under the composer;
    // `100vw` ignores a scrollbar's width and can force sideways scroll.
    return (
      <div className="h-viewport w-full overflow-hidden">
        <Helmet>
          <title>{getTabTitle(pathname)}</title>
        </Helmet>
        <Outlet />
      </div>
    );
  }

  // Built once: the same bar is either a resizable column (pointer layout) or
  // the contents of a sheet (mobile), never both — two copies would mount two
  // infinite-scroll listeners onto the same query.
  const notificationBar = (
    <NotificationBar
      isOpen={notificationBarOpen}
      onClose={() => setNotificationBarOpen(false)}
      notifications={notifications}
      typeConfig={NOTIFICATION_TYPE_CONFIG}
      onNotificationClick={handleNotificationClick}
      onMarkRead={(n) =>
        new Promise<void>((resolve, reject) => {
          markAsRead(n.id, {
            onSuccess: () => resolve(),
            onError: (err) => reject(err),
          });
        })
      }
      onMarkUnread={(n) =>
        new Promise<void>((resolve, reject) => {
          markAsUnread(n.id, {
            onSuccess: () => resolve(),
            onError: (err) => reject(err),
          });
        })
      }
      onMarkAllRead={() => markAllAsRead()}
      isMarkAllReadLoading={isMarkAllAsReadPending}
      hasMore={Boolean(hasNextPage)}
      isLoadingMore={Boolean(isFetchingNextPage)}
      onLoadMore={() => {
        void (fetchNextPage as () => Promise<unknown>)();
      }}
      isLoading={isNotificationsLoading}
      className={isMobileLayout ? undefined : "mt-5"}
      isStatic
    />
  );

  return (
    // `h-[100dvh]`, not `h-screen`: the document is pinned to the dynamic
    // viewport (see index.css), so a `100vh` frame would be ~40px taller than
    // the body on mobile and its last 40px would be clipped with no document
    // scroll left to reach it.
    <div className="flex h-viewport overflow-hidden">
      <Helmet>
        <title>{getTabTitle(pathname)}</title>
      </Helmet>
      {/* Default position is the launcher's corner, so the arrow lands on top
          of the glasses. Stack it above the 56px disc on exactly the condition
          that renders the launcher — which includes the panel being closed
          (the launcher unmounts while it's open), and mirrors the launcher's
          640px move to right/bottom 16. */}
      <FloatButton.BackTop
        target={() =>
          document.querySelector("main") as Document | HTMLElement | Window
        }
        style={
          canUseIndy && !chatbotOpen
            ? isNarrowViewport
              ? { right: 16, bottom: 88 }
              : { bottom: 96 }
            : undefined
        }
      />
      {isMobileLayout ? (
        // One navbar, in a sheet — not a second copy hidden with `md:hidden`,
        // which would leave two of every nav link in the accessibility tree.
        // The sheet supplies the backdrop, Esc, and the focus trap.
        <MobileSheet
          open={isNavSheetOpen}
          onClose={() => setIsNavSheetOpen(false)}
          placement="left"
          ariaLabel="Main navigation"
          showCloseButton={false}
          dataTestId="MainLayout-nav-sheet"
        >
          <Navbar
            tabs={navbarTabs}
            isOffCanvas
            onNavigate={() => setIsNavSheetOpen(false)}
            profileImageUrl={user?.avatarUrl || undefined}
            profileName={`${user?.firstName || ""} ${user?.lastName || ""}`}
            profileUrl="/"
            profileMenuItems={profileMenuItems}
            adminSection={adminSection}
            profileWarning={profileWarning}
          />
        </MobileSheet>
      ) : (
        <Navbar
          tabs={navbarTabs}
          pinned={navbarPinned}
          isPinAllowed={!(sidebarPinned && isAppointmentDetailsPage)}
          onPinChange={setNavbarPinned}
          profileImageUrl={user?.avatarUrl || undefined}
          profileName={`${user?.firstName || ""} ${user?.lastName || ""}`}
          profileUrl="/"
          profileMenuItems={profileMenuItems}
          adminSection={adminSection}
          profileWarning={profileWarning}
        />
      )}
      <ImpersonationBanner />
      <SandboxOverrideBanner />

      {/* Content area — sits to the right of the fixed navbar.
          Column-flex so BrowserWarningBanner can stack above the content row. */}
      <div
        className={clsx(
          "flex flex-col flex-1 h-viewport min-w-0 transition-all duration-300",
          navbarMargin,
        )}
      >
        {/* Mobile app bar. With the navbar rail gone there is nothing on
            screen that opens navigation, so the burger has to live in the
            layout — the pages have no shared chrome of their own to host it.
            Above `md` this does not render and the rail is the entry point. */}
        {isMobileLayout && (
          // Burger only, no title: every page renders its own `PageHeader`
          // immediately below, and a bar naming the same view in different
          // words ("Appointments" over "All appointments") reads as two
          // places rather than one.
          <header className="flex h-12 shrink-0 items-center px-2">
            <IconButton
              icon={<Icon name="left-sidebar-icon" size="m" />}
              size="lg"
              variant="ghost"
              ariaLabel="Open navigation"
              onClick={() => setIsNavSheetOpen(true)}
              data-testid="MainLayout-open-nav-btn"
            />
          </header>
        )}
        <SessionExpiredBanner />
        <BrowserWarningBanner />
        <div className="flex flex-1 min-h-0">
          <PanelGroup direction="horizontal">
            {/* Notification bar panel (collapsible, feature-flagged).
                Pointer layouts only — see the sheet below the panel group for
                what the mobile layout does instead. */}
            {isNotificationsFeatureEnabled && !isMobileLayout && (
              <>
                <Panel
                  id="notifications-panel"
                  ref={notificationPanelRef}
                  order={1}
                  defaultSize={0}
                  minSize={NOTIFICATION_BAR_MIN_SIZE}
                  maxSize={NOTIFICATION_BAR_MAX_SIZE}
                  collapsible
                  collapsedSize={0}
                  // Intentionally NOT wiring onCollapse/onExpand to the atom.
                  // react-resizable-panels fires these whenever the library
                  // recomputes layout constraints (e.g. when the main content
                  // panel briefly can't satisfy its own minSize during a heavy
                  // first-time route mount). Treating those as user intent
                  // would spuriously close the notification bar after
                  // navigation. The atom is the single source of truth; the
                  // sync effect below re-expands the panel if the library
                  // force-collapsed it. User dismissal goes through the
                  // explicit close button or the Notifications tab toggle.
                >
                  {notificationBar}
                </Panel>

                {/* Resize handle — only visible when notification bar is open */}
                {notificationBarOpen && (
                  <PanelResizeHandle
                    hitAreaMargins={{ coarse: 0, fine: 0 }}
                    style={{ cursor: "col-resize" }}
                    className="w-[6px] shrink-0 hover:bg-[var(--indigo-gray-100)] active:bg-[var(--indigo-gray-200)] transition-colors duration-150"
                  />
                )}
              </>
            )}

            {/* Main content panel */}
            <Panel
              id="main-content-panel"
              order={isNotificationsFeatureEnabled ? 2 : 1}
              minSize={40}
            >
              <main
                className={clsx(
                  "main-layout-container h-full flex-1 [scrollbar-gutter:stable] min-w-0",
                  (!isAppointmentDetailsPage || isNewAppointmentPage) &&
                    "overflow-y-auto",
                  sidebarPinned && "has-sidebar-pinned",
                )}
              >
                {/* The mobile layout's app bar above already separates the
                    content from the top of the window. */}
                {hasTopSpacer && (
                  <div className={isMobileLayout ? "h-2" : "h-6"} />
                )}
                <Outlet />
              </main>
            </Panel>
          </PanelGroup>
        </div>
      </div>

      {/* Notifications on the mobile layout.

          The panel above splits the row horizontally, and there is no width
          on a phone to split: the bar collapsed to a few characters and its
          content wrapped one letter per line. A sheet gives it the full
          screen and leaves the page underneath at its own width.

          `showCloseButton={false}` — NotificationBar draws its own header
          with a close control, and two of them in one corner is one too
          many. */}
      {isNotificationsFeatureEnabled && isMobileLayout && (
        <MobileSheet
          open={notificationBarOpen}
          onClose={() => setNotificationBarOpen(false)}
          placement="right"
          widthPercent={92}
          ariaLabel="Notifications"
          showCloseButton={false}
          dataTestId="MainLayout-notifications-sheet"
        >
          {notificationBar}
        </MobileSheet>
      )}

      {/* User Settings Modal */}
      {userGroup && (
        <UserSettingsModal
          isOpen={isSettingsModalOpen}
          onClose={() => setIsSettingsModalOpen(false)}
          userGroup={userGroup}
        />
      )}

      {/* Keyboard Shortcuts Modal */}
      <KeyboardShortcutsModal
        isOpen={isShortcutsModalOpen}
        onClose={() => setIsShortcutsModalOpen(false)}
      />

      {/* Global Indy chatbot — persists across navigation. Unmounted on the
          full-screen /indy route so only one useAgentChat instance (and one SSE
          stream) is live at a time. */}
      {canUseIndy && pathname !== INDY_PATH && (
        <ChatbotModal
          open={chatbotOpen}
          onClose={() => setChatbotOpen(false)}
          context={chatContext}
        />
      )}

      {/* Bottom-right launcher — visible only when the panel is closed. */}
      {canUseIndy && pathname !== INDY_PATH && !chatbotOpen && (
        <ChatbotLauncher onOpen={() => setChatbotOpen(true)} />
      )}
    </div>
  );
};

export default MainLayout;
